import React from "react";
import theme from "../../../styles/theme";
import drawSpaceConfig from "../config/drawSpaceConfig";
import DrawProperties from "./DrawProperties";

/*
====================================================================
HELPERS DE TERMINOLOGIE
====================================================================
*/

const getPanelTitle = (activeMode) => {
  if (activeMode === "SITE") return "Ouvrage Properties";
  if (activeMode === "OUVRAGE") return "Level Properties";
  if (activeMode === "NIVEAU") return "Area Properties";
  return drawSpaceConfig.labels.properties;
};

const getNameFieldLabel = (activeMode) => {
  if (activeMode === "SITE") return "Ouvrage";
  if (activeMode === "OUVRAGE") return "Level";
  if (activeMode === "NIVEAU") return "Area";
  return "Label";
};

/*
Type métier lisible
- structure : OUVRAGE / LEVEL
- niveau : AREA_*
*/
const getReadableType = (entity, activeMode) => {
  if (!entity) return "";

  if (entity.type === "OUVRAGE") return "Ouvrage";
  if (entity.type === "LEVEL") return "Level";

  if (
    entity.type === drawSpaceConfig.entityTypes.AREA_RECT ||
    entity.type === drawSpaceConfig.entityTypes.AREA_POLYLINE ||
    entity.type === drawSpaceConfig.entityTypes.CIRCLE ||
    entity.type === drawSpaceConfig.entityTypes.LINE ||
    entity.type === drawSpaceConfig.entityTypes.ARC
  ) {
    return "Area";
  }

  if (activeMode === "SITE") return "Ouvrage";
  if (activeMode === "OUVRAGE") return "Level";
  if (activeMode === "NIVEAU") return "Area";

  return entity.type ?? "";
};

/*
Type géométrique lisible
*/
const getReadableGeometryType = (entity) => {
  if (!entity) return "";

  if (entity.type === "OUVRAGE" || entity.type === "LEVEL") {
    if (entity.geometry?.points) return "Polyline";
    if (
      "x" in (entity.geometry ?? {}) &&
      "y" in (entity.geometry ?? {}) &&
      "width" in (entity.geometry ?? {}) &&
      "height" in (entity.geometry ?? {})
    ) {
      return "Rectangle";
    }
    return "";
  }

  if (entity.type === drawSpaceConfig.entityTypes.AREA_RECT) return "Rectangle";
  if (entity.type === drawSpaceConfig.entityTypes.AREA_POLYLINE) return "Polyline";
  if (entity.type === drawSpaceConfig.entityTypes.CIRCLE) return "Circle";
  if (entity.type === drawSpaceConfig.entityTypes.LINE) return "Line";
  if (entity.type === drawSpaceConfig.entityTypes.ARC) return "Arc";

  return "";
};

/*
Un level structurel peut avoir elevation au niveau racine,
une area l’a plutôt dans attributes.
*/
const getElevationValue = (entity) => {
  if (!entity) return "";
  if (entity.elevation !== undefined && entity.elevation !== null) {
    return entity.elevation;
  }
  return entity.attributes?.elevation ?? "";
};

/*
====================================================================
COMPOSANT PRINCIPAL
====================================================================
*/

const DrawPropertiesPanel = ({
  entity,
  activeMode = "NIVEAU",
  onChange,
  style = {},
}) => {
  const geometry = entity?.geometry ?? {};
  const attributes = entity?.attributes ?? {};

  const panelTitle = getPanelTitle(activeMode);
  const nameFieldLabel = getNameFieldLabel(activeMode);
  const readableType = getReadableType(entity, activeMode);
  const readableGeometryType = getReadableGeometryType(entity);

  if (!entity) {
    return (
      <div
        style={{
          width: 260,
          borderLeft: `1px solid ${theme.colors.borders.panel}`,
          background: theme.colors.background.surface,
          padding: theme.spacing.sm,
          fontFamily: theme.fonts.body,
          fontSize: theme.fontSizes.sm,
          color: theme.colors.text.small,
          boxSizing: "border-box",
          ...style,
        }}
      >
        {drawSpaceConfig.labels.noSelection}
      </div>
    );
  }

  /*
  ================================================================
  HELPERS DE MISE À JOUR
  ================================================================
  */

  const updateField = (field, value) => {
    if (!onChange) return;

    onChange({
      ...entity,
      [field]: value,
    });
  };

  const updateGeometry = (field, value) => {
    if (!onChange) return;

    onChange({
      ...entity,
      geometry: {
        ...geometry,
        [field]: value === "" ? "" : Number(value),
      },
    });
  };

  const updateAttributes = (field, value) => {
    if (!onChange) return;

    /*
    Pour LEVEL structurel, on peut porter elevation au niveau racine.
    Pour AREA, on garde attributes.elevation.
    */
    if (entity.type === "LEVEL" && field === "elevation") {
      onChange({
        ...entity,
        elevation: value === "" ? "" : Number(value),
      });
      return;
    }

    onChange({
      ...entity,
      attributes: {
        ...attributes,
        [field]: value === "" ? "" : Number(value),
      },
    });
  };

  return (
    <div
      style={{
        width: 260,
        borderLeft: `1px solid ${theme.colors.borders.panel}`,
        background: theme.colors.background.surface,
        padding: theme.spacing.sm,
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing.xs,
        boxSizing: "border-box",
        ...style,
      }}
    >
      <div
        style={{
          fontFamily: theme.fonts.heading,
          fontSize: theme.fontSizes.sm,
          fontWeight: theme.fontWeights.semiBold,
          color: theme.colors.text.primary,
          marginBottom: theme.spacing.xs,
        }}
      >
        {panelTitle}
      </div>

      <DrawProperties
        label="ID"
        value={entity.id ?? ""}
        readOnlyMode={true}
        labelWidth={70}
      />

      <DrawProperties
        label="Type"
        value={readableType}
        readOnlyMode={true}
        labelWidth={70}
      />

      <DrawProperties
        label="Geometry"
        value={readableGeometryType}
        readOnlyMode={true}
        labelWidth={70}
      />

      <DrawProperties
        label={nameFieldLabel}
        value={entity.label ?? ""}
        onChange={(value) => updateField("label", value)}
        labelWidth={70}
      />

      {"width" in geometry && (
        <DrawProperties
          label="Width"
          value={geometry.width}
          onChange={(value) => updateGeometry("width", value)}
          labelWidth={70}
          type="number"
        />
      )}

      {"height" in geometry && (
        <DrawProperties
          label="Height"
          value={geometry.height}
          onChange={(value) => updateGeometry("height", value)}
          labelWidth={70}
          type="number"
        />
      )}

      {"x" in geometry && (
        <DrawProperties
          label="X"
          value={geometry.x}
          onChange={(value) => updateGeometry("x", value)}
          labelWidth={70}
          type="number"
        />
      )}

      {"y" in geometry && (
        <DrawProperties
          label="Y"
          value={geometry.y}
          onChange={(value) => updateGeometry("y", value)}
          labelWidth={70}
          type="number"
        />
      )}

      {"r" in geometry && (
        <DrawProperties
          label="Radius"
          value={geometry.r}
          onChange={(value) => updateGeometry("r", value)}
          labelWidth={70}
          type="number"
        />
      )}

      {"cx" in geometry && (
        <DrawProperties
          label="Center X"
          value={geometry.cx}
          onChange={(value) => updateGeometry("cx", value)}
          labelWidth={70}
          type="number"
        />
      )}

      {"cy" in geometry && (
        <DrawProperties
          label="Center Y"
          value={geometry.cy}
          onChange={(value) => updateGeometry("cy", value)}
          labelWidth={70}
          type="number"
        />
      )}

      {"x1" in geometry && (
        <DrawProperties
          label="X1"
          value={geometry.x1}
          onChange={(value) => updateGeometry("x1", value)}
          labelWidth={70}
          type="number"
        />
      )}

      {"y1" in geometry && (
        <DrawProperties
          label="Y1"
          value={geometry.y1}
          onChange={(value) => updateGeometry("y1", value)}
          labelWidth={70}
          type="number"
        />
      )}

      {"x2" in geometry && (
        <DrawProperties
          label="X2"
          value={geometry.x2}
          onChange={(value) => updateGeometry("x2", value)}
          labelWidth={70}
          type="number"
        />
      )}

      {"y2" in geometry && (
        <DrawProperties
          label="Y2"
          value={geometry.y2}
          onChange={(value) => updateGeometry("y2", value)}
          labelWidth={70}
          type="number"
        />
      )}

      <DrawProperties
        label="Elevation"
        value={getElevationValue(entity)}
        onChange={(value) => updateAttributes("elevation", value)}
        labelWidth={70}
        type="number"
      />
    </div>
  );
};

export default DrawPropertiesPanel;